
import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  useTheme, useMediaQuery, Box
} from '@mui/material';
import { colors, spacing } from 'common/theme';
import { useCouponsFilter } from './hooks/useCouponsFilter';
import { useCouponsData } from './hooks/useCouponsData';
import { COUPON_TABS, FILTER_BENEFITS_TYPES } from '../../utils/enums';
import { FilterBottomSheet } from 'components/common/FilterBottomSheet';
import { ArchiveModal } from 'components/common/ArchiveModal';
import { DeleteModal } from 'components/common/DeleteModal';
import { GeneralModal } from 'components/common/GeneralModal';
import EmptyDataHot from 'components/EmptyDataHot';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import { TextHot } from 'components/common/TextHot';
import BenefitTableRow from './BenefitTableRow';
import { TabsSearchHeader } from './TabsSearchHeader';
import { BenefitCard } from './BenefitCard';
import Loader from '../../../../components/common/loader';
import { generateTestId } from 'app/functions';
import { useTranslations } from 'common/hooks/useTranslations';
const INITIAL_FILTERS = {
  selectedFilter: FILTER_BENEFITS_TYPES.ACTIVE,
};

export default function CouponsTableSection({ testID }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const loadMoreTriggerRef = useRef(null);
  const [expandedBenefitId, setExpandedBenefitId] = useState(null);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const { translate } = useTranslations();
  const filterHook = useCouponsFilter(INITIAL_FILTERS);
  const dataHook = useCouponsData();

  useEffect(() => {
    if (!filterHook.filterOpen) {
      filterHook.discardPendingChanges();
    }
  }, [filterHook.filterOpen]);
  const couponUpdateCallbacksRef = useRef(new Map());
  const couponArchiveCallbacksRef = useRef(new Map());

  const handleCouponStatusUpdate = useCallback((benefitId, updateFn) => {
    if (updateFn) {
      couponUpdateCallbacksRef.current.set(benefitId, updateFn);
    } else {
      couponUpdateCallbacksRef.current.delete(benefitId);
    }
  }, []);

  const updateCouponStatus = useCallback((benefitId, couponCode, newStatus, activeFilters) => {
    const updateFn = couponUpdateCallbacksRef.current.get(benefitId);
    if (updateFn) {
      updateFn(benefitId, couponCode, newStatus, activeFilters);
    }
  }, []);

  const handleCouponArchiveUpdate = useCallback((benefitId, updateFn) => {
    if (updateFn) {
      couponArchiveCallbacksRef.current.set(benefitId, updateFn);
    } else {
      couponArchiveCallbacksRef.current.delete(benefitId);
    }
  }, []);

  const updateCouponArchive = useCallback((benefitId, couponCode, isArchived) => {
    const updateFn = couponArchiveCallbacksRef.current.get(benefitId);
    if (updateFn) {
      updateFn(benefitId, couponCode, isArchived);
    }
  }, []);

  useEffect(() => {
    const target = loadMoreTriggerRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        const isPaid = filterHook.activeTab === COUPON_TABS.PAID;
        const activeRefs = isPaid ? dataHook.paginationRefs.paid : dataHook.paginationRefs.downloaded;

        if (activeRefs.hasMore.current && !activeRefs.loading.current) {
          if (isPaid) {
            const { selectedFilter } = filterHook.appliedPaidFilters || {};
            dataHook.fetchPaidCoupons(
              activeRefs.page.current + 1,
              false,
              selectedFilter
            );
          } else {
            dataHook.fetchDownloadedCoupons(activeRefs.page.current + 1, false);
          }
        }
      }
    }, { threshold: 0, rootMargin: '150px' });

    observer.observe(target);
    return () => observer.disconnect();
  }, [
    filterHook.activeTab,
    dataHook,
    filterHook.appliedPaidFilters
  ]);

  useEffect(() => {
    if (filterHook.activeTab === COUPON_TABS.PAID) {
      const { selectedFilter } = filterHook.appliedPaidFilters || {};
      dataHook.fetchPaidCoupons(0, true, selectedFilter);
    } else {
      dataHook.fetchDownloadedCoupons(0, true);
    }
  }, [filterHook.activeTab, filterHook.appliedPaidFilters]);

  useEffect(() => {
    setExpandedBenefitId(null);
  }, [filterHook.activeTab, filterHook.appliedPaidFilters]);

  const rawBenefits = filterHook.activeTab === COUPON_TABS.PAID
    ? dataHook.paidBenefitsData
    : dataHook.downloadedBenefitsDataApi;

  const currentBenefits = filterHook.filterBenefits(
    rawBenefits,
    filterHook.appliedFilters,
    filterHook.activeTab === COUPON_TABS.PAID
  );

  const paidCount = filterHook.filterBenefits(
    dataHook.paidBenefitsData,
    filterHook.appliedPaidFilters,
    true
  ).reduce((acc, b) => acc + b.count, 0);

  const downloadedCount = filterHook.filterBenefits(
    dataHook.downloadedBenefitsDataApi,
    {},
    false
  ).reduce((acc, b) => acc + b.count, 0);

  const hasRawBenefits = rawBenefits?.length > 0 && rawBenefits.some(b => {
    if (filterHook.activeTab === COUPON_TABS.PAID) {
      return b.count > 0;
    } else {
      return b.orders?.length > 0;
    }
  });

  const showEmptyState = !currentBenefits?.length && !dataHook.isLoading;
  const isSearchOrFilterActive = filterHook.searchText?.trim() || 
    (filterHook.activeTab === COUPON_TABS.PAID && 
     filterHook.appliedPaidFilters?.selectedFilter !== FILTER_BENEFITS_TYPES.ACTIVE);

  return (
    <>
      {dataHook.isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><Loader /></Box>}


      {!dataHook.isLoading && (
        <TabsSearchHeader
          testID={testID}
          paidCount={paidCount}
          downloadedCount={downloadedCount}
          filterHook={filterHook}
        />
      )}

      {showEmptyState && (
        <EmptyDataHot 
          title={
            hasRawBenefits && isSearchOrFilterActive
              ? translate('no_benefits_found_title')
              : translate('no_benefits_from_api_title')
          } 
          image={NotInterestedIcon} 
          imageSize={spacing.xxxxl} 
        />
      )}

      {currentBenefits?.length > 0 && !isMobile && (
        <TableContainer
          testId={generateTestId(testID, 'table-container')}
          component={Paper}
          sx={{
            backgroundColor: colors.system.surface.subtle,
            direction: 'rtl',
            padding: `${spacing.md}px`,
            boxShadow: 'none',
            border: 'none'
          }}
        >
          <Table
            testId={generateTestId(testID, 'table')}
            sx={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: `0 ${spacing.xs}px`
            }}
          >
            <TableHead testId={generateTestId(testID, 'table-head')}>
              <TableRow testId={generateTestId(testID, 'table-row')}>
                <TableCell testId={generateTestId(testID, 'header-toggle')} sx={{ border: 'none', width: '50px' }} />
                {filterHook.activeTab === COUPON_TABS.PAID && (
                  <TableCell testId={generateTestId(testID, 'header-savings')} align="left" sx={{ border: 'none' }}>
                    <TextHot testID={generateTestId(testID, 'header-savings-text')} size="l" color="primary">{translate('my_coupons_table_header_total_savings')}</TextHot>
                  </TableCell>
                )}
                <TableCell testId={generateTestId(testID, 'header-latest-order')} align="left" sx={{ border: 'none' }}>
                  <TextHot testID={generateTestId(testID, 'header-latest-order-text')} size="l" color="primary">{translate('my_coupons_table_header_latest_order')}</TextHot>
                </TableCell>
                <TableCell testId={generateTestId(testID, 'header-order-count')} align="left" sx={{ border: 'none' }}>
                  <TextHot testID={generateTestId(testID, 'header-order-count-text')} size="l" color="primary">{translate('my_coupons_table_header_order_count')}</TextHot>
                </TableCell>
                <TableCell testId={generateTestId(testID, 'header-benefit-name')} align="left" sx={{ border: 'none' }}>
                  <TextHot testID={generateTestId(testID, 'header-benefit-name-text')} size="l" color="primary">{translate('my_coupons_table_header_benefit_name')}</TextHot>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody testId={generateTestId(testID, 'table-body')}>
              {currentBenefits.map(row => (
                <BenefitTableRow
                  key={row.name + '-' + row.id}
                  testId={generateTestId(testID, `benefit-row-${row.id}`)}
                  actionsBaseTestID={generateTestId(testID, 'actions')}
                  benefit={row}
                  isExpanded={expandedBenefitId === row.id}
                  onToggle={() => setExpandedBenefitId(expandedBenefitId === row.id ? null : row.id)}
                  onArchive={filterHook.activeTab === COUPON_TABS.PAID ? (c) => {
                    filterHook.setSelectedCoupon({ ...c, benefitId: row.id, code: c.code || c.couponCode || c.id });
                    filterHook.setArchiveModalOpen(true);
                  } : undefined}
                  onCancel={(c, benefitId, e) => {
                    filterHook.setSelectedCoupon({ ...c, benefitId: row.id });
                    filterHook.setCancelAnchorEl(e.currentTarget);
                    filterHook.setCancelModalOpen(true);
                  }}
                  activeFilters={filterHook.appliedFilters}
                  activeCancelCouponId={filterHook.selectedCoupon?.benefitId}
                  isPaidCoupons={filterHook.activeTab === COUPON_TABS.PAID}
                  onCouponStatusUpdate={handleCouponStatusUpdate}
                  onCouponArchiveUpdate={handleCouponArchiveUpdate}
                />
              ))}
            </TableBody>
          </Table>
          <div ref={loadMoreTriggerRef} />
        </TableContainer>
      )}

      {/* Mobile Cards */}
      {currentBenefits?.length > 0 && isMobile && (
        <Box sx={{
          display: { xs: 'flex', lg: 'none' },
          flexDirection: 'column',
          gap: '4px',
          p: '4px',
          m: `${spacing.md}px`,
          backgroundColor: colors.system.surface.subtle,
          borderRadius: '16px'
        }}>
          {dataHook.isLoading && currentBenefits.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><Loader /></Box>
          ) : (
            currentBenefits.map(b => (
              <BenefitCard
                key={b.name + '-' + b.id}
                benefit={b}
                testID={generateTestId(testID, `benefit-card-${b.id}`)}
                actionsBaseTestID={generateTestId(testID, 'actions')}
                isExpanded={expandedBenefitId === b.id}
                onToggle={() => setExpandedBenefitId(expandedBenefitId === b.id ? null : b.id)}
                onArchive={filterHook.activeTab === COUPON_TABS.PAID ? (c) => {
                  filterHook.setSelectedCoupon({ ...c, benefitId: b.id, code: c.code || c.couponCode || c.id });
                  filterHook.setArchiveModalOpen(true);
                } : undefined}
                onCancel={(c, e) => {
                  filterHook.setSelectedCoupon({ ...c, benefitId: b.id });
                  filterHook.setCancelAnchorEl(e.currentTarget);
                  filterHook.setCancelModalOpen(true);
                }}
                activeFilters={filterHook.appliedFilters}
                activeCancelCouponId={filterHook.selectedCoupon?.benefitId}
                isPaidCoupons={filterHook.activeTab === COUPON_TABS.PAID}
                onCouponStatusUpdate={handleCouponStatusUpdate}
                onCouponArchiveUpdate={handleCouponArchiveUpdate}
              />
            ))
          )}
        </Box>
      )}

      {/* Filter Bottom Sheet - Only show for paid coupons */}
      {filterHook.activeTab === COUPON_TABS.PAID && (
        <FilterBottomSheet
          testID={generateTestId(testID, 'filter-bottom-sheet')}
          open={filterHook.filterOpen}
          onClose={() => {
            filterHook.discardPendingChanges();
            filterHook.setFilterOpen(false);
          }}
          filters={filterHook.pendingFilters}
          onFilterChange={(selectedFilter) => filterHook.toggleFilter(selectedFilter, true)}
          onApply={() => {
            filterHook.applyFilters();
            filterHook.setFilterOpen(false);
            const { selectedFilter } = filterHook.pendingFilters;
            dataHook.fetchPaidCoupons(0, true, selectedFilter);
          }}
          onReset={() => filterHook.resetFilters()}
          anchorEl={filterHook.filterAnchorEl}
          isMobile={isMobile}
          filterMode="paid"
          filterCounts={dataHook.filterCounts}
        />
      )}

      {/* Modals */}
      <ArchiveModal
        testID={generateTestId(testID, 'archive-modal')}
        open={filterHook.archiveModalOpen}
        onClose={() => filterHook.setArchiveModalOpen(false)}
        isPaid={filterHook.activeTab === COUPON_TABS.PAID}
        couponData={filterHook.selectedCoupon}
        onFailure={(message) => {
          filterHook.setArchiveModalOpen(false);
          setResponseMessage(message);
          setResponseModalOpen(true);
        }}
        onSuccess={async (newStatus) => {
          filterHook.setArchiveModalOpen(false);
          setResponseMessage(newStatus ? translate('coupon_archived_success') : translate('coupon_unarchived_success'));
          setResponseModalOpen(true);

          if (filterHook.selectedCoupon) {
            const benefitId = filterHook.selectedCoupon.benefitId;
            const couponCode = filterHook.selectedCoupon.code || filterHook.selectedCoupon.couponCode || filterHook.selectedCoupon.id;
            updateCouponArchive(benefitId, couponCode, newStatus);
          }
          
          const currentFilter = filterHook.appliedPaidFilters?.selectedFilter;
          const updatedCounts = await dataHook.refreshData(
            filterHook.activeTab === COUPON_TABS.PAID,
            filterHook.appliedPaidFilters,
            {}
          );
          
          if (!newStatus && currentFilter === FILTER_BENEFITS_TYPES.ARCHIVED) {
            if (updatedCounts?.totalArchivedBenefitsCount === 0) {
              filterHook.setFilterDirectly(FILTER_BENEFITS_TYPES.ACTIVE);
              dataHook.fetchPaidCoupons(0, true, FILTER_BENEFITS_TYPES.ACTIVE);
            }
          }
        }}
      />

      <DeleteModal
        testID={generateTestId(testID, 'delete-modal')}
        open={filterHook.cancelModalOpen}
        onClose={() => filterHook.setCancelModalOpen(false)}
        couponData={filterHook.selectedCoupon}
        onFailure={(message) => {
          filterHook.setCancelModalOpen(false);
          setResponseMessage(message);
          setResponseModalOpen(true);
        }}
        onSuccess={async (message, newStatus) => {
          filterHook.setCancelModalOpen(false);
          setResponseMessage(message);
          setResponseModalOpen(true);
          if (filterHook.selectedCoupon && newStatus) {
            const couponCode = filterHook.selectedCoupon.code || filterHook.selectedCoupon.couponCode || filterHook.selectedCoupon.id;
            const currentFilters = filterHook.activeTab === COUPON_TABS.PAID 
              ? filterHook.appliedPaidFilters 
              : {};
            updateCouponStatus(
              filterHook.selectedCoupon.benefitId,
              couponCode,
              newStatus,
              currentFilters
            );
          }
          
          const currentFilter = filterHook.appliedPaidFilters?.selectedFilter;
          const updatedCounts = await dataHook.refreshData(
            filterHook.activeTab === COUPON_TABS.PAID,
            filterHook.appliedPaidFilters,
            {}
          );
          
          if (newStatus && currentFilter === FILTER_BENEFITS_TYPES.ACTIVE) {
            if (updatedCounts?.totalActiveBenefitsCount === 0) {
              filterHook.setFilterDirectly(FILTER_BENEFITS_TYPES.CANCELLED);
              dataHook.fetchPaidCoupons(0, true, FILTER_BENEFITS_TYPES.CANCELLED);
            }
          }
        }}
        anchorEl={filterHook.cancelAnchorEl}
      />

      <GeneralModal
        testID={generateTestId(testID, 'general-modal')}
        open={responseModalOpen}
        onClose={() => setResponseModalOpen(false)}
        buttonText={translate('confirm_button_text')}
      >
        {responseMessage}
      </GeneralModal>
    </>
  );
}
